<template>
  <div class="login-layout">
    <!-- ── OAuth2: pantalla de redirección ── -->
    <template v-if="useOAuth2">
      <div class="oauth2-screen">
        <div class="oauth2-screen__panel">
          <div class="oauth2-screen__logo">
            <img src="../../assets/circulo.png" height="72" width="72" />
          </div>
          <h2 class="oauth2-screen__title">🔐 Redirigiendo...</h2>
          <p class="oauth2-screen__text">
            Estás siendo redirigido al sistema de autenticación.
          </p>
          <div class="oauth2-screen__spinner"></div>
        </div>
      </div>
    </template>

    <!-- ── Login normal ── -->
    <template v-else>
      <!-- Panel izquierdo: imagen -->
      <div class="login-photo">
        <div class="login-photo__overlay"></div>
        <div class="login-photo__brand">
          © 720tec · Sistema de Grabación de Cargas
        </div>
      </div>

      <!-- Panel derecho: formulario -->
      <div class="login-form-side">
        <!-- Acento superior -->
        <div class="login-form-side__accent"></div>

        <!-- Logo -->
        <div class="login-logo">
          <div class="login-logo__circle">
            <img
              src="../../assets/circulo.png"
              height="46"
              width="46"
              style="display: block"
            />
          </div>
          <div class="login-logo__sub">{{ $t("login.wellcome") }}</div>
          <div class="login-logo__main">{{ $t("login.company") }}</div>
        </div>

        <!-- Formulario -->
        <div class="login-form">
          <!-- Error -->
          <div class="login-error" :class="{ 'login-error--visible': error }">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="2"
              />
              <line
                x1="12"
                y1="8"
                x2="12"
                y2="12"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
              <circle cx="12" cy="16" r="1" fill="currentColor" />
            </svg>
            {{ $t("login.invalidLogin") }}
          </div>

          <!-- Usuario -->
          <div class="login-field">
            <label class="login-field__label">{{ $t("login.username") }}</label>
            <el-input
              type="text"
              :placeholder="$t('login.username')"
              v-model="login.username"
              :class="{ 'login-input--error': error }"
              class="login-input"
              @keydown.native="loginUser"
            />
          </div>

          <!-- Contraseña -->
          <div class="login-field">
            <label class="login-field__label">{{ $t("login.password") }}</label>
            <el-input
              :placeholder="$t('login.password')"
              v-model="login.pass"
              type="password"
              show-password
              :class="{ 'login-input--error': error }"
              class="login-input"
              @keydown.native="loginUser"
            />
          </div>

          <!-- Botón -->
          <button class="login-btn" @click="loginUser('click')">
            {{ $t("login.login") }}
          </button>
        </div>

        <!-- Footer -->
        <div class="login-footer">
          {{ $t("login.company") }} · Grabaciones &nbsp;
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import http from "axios";
import { mapState } from "vuex";
import storage from "../utils/storage";
import authService from "@/services/auth.service";

export default {
  name: "LoginView",

  async mounted() {
    // Cargar nombre de empresa desde config.json (configurable en Docker)
    try {
      const res = await fetch("/config.json");
      const cfg = await res.json();
      if (cfg.companyName) {
        this.companyName = cfg.companyName;
      }
    } catch (e) {
      console.warn("No se pudo cargar config.json, usando nombre por defecto");
    }

    if (this.useOAuth2) {
      console.log("⚠️ Login no disponible con OAuth2, redirigiendo...");
      setTimeout(() => {
        window.location.href = "/oauth2/start";
      }, 1500);
      return;
    }

    this.authenticated = storage.loadAuthenticated();
    if (this.authenticated === "true") {
      this.$router.replace({ name: "Orders" });
    }
  },

  data() {
    return {
      API_URL: process.env.VUE_APP_API_URL,
      login: {
        username: "",
        pass: "",
      },
      authenticated: false,
      error: false,
      useOAuth2: authService.isUsingOAuth2(),
      companyName: "Grabaciones 720tec",
    };
  },

  computed: {
    ...mapState("config", ["client"]),
  },

  methods: {
    loginUser: function (event) {
      if (event === "click" || event.which === 13) {
        var self = this;
        if (this.login.username !== "" && this.login.pass !== "") {
          var url = `${self.API_URL}/auth/login`;
          http
            .post(url, { username: this.login.username, pass: this.login.pass })
            .then((response) => {
              if (response.status === 200) {
                storage.saveToken(
                  response.data.token,
                  response.data.user,
                  response.data.role,
                  response.data.group
                );
                storage.saveAuthenticated(true);
                this.$emit("authenticated", true);
                this.$router.replace({ name: "Orders" });
              }
            })
            .catch((error) => {
              self.error = error;
              const status = error.response?.status;
              if (status === 401) {
                self.$message({
                  message: this.$t("login.invalidLogin"),
                  type: "error",
                });
              } else if (status === 500) {
                self.$message({
                  message: "No se ha podido conectar con el servidor",
                  type: "error",
                });
              } else {
                self.$message({
                  message: "Ha ocurrido un error inesperado",
                  type: "error",
                });
              }
            });
        }
      }
    },
  },
};
</script>

<style scoped>
/* ── Reset & base ─────────────────────────────────────────────── */
* {
  box-sizing: border-box;
}

/* ── Layout dos columnas ──────────────────────────────────────── */
.login-layout {
  display: flex;
  height: 100vh;
  overflow: hidden;
  font-family: "DM Sans", "Roboto", sans-serif;
}

/* ── Panel izquierdo: foto ────────────────────────────────────── */
.login-photo {
  flex: 1;
  position: relative;
  background-image: url("../../assets/fondo.jpg");
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  overflow: hidden;
}

.login-photo__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
      to right,
      rgba(15, 23, 42, 0) 55%,
      rgba(15, 23, 42, 0.85) 100%
    ),
    linear-gradient(to bottom, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.4));
  pointer-events: none;
}

.login-photo__brand {
  position: absolute;
  bottom: 28px;
  left: 28px;
  z-index: 2;
  color: rgba(255, 255, 255, 0.45);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.04em;
}

/* ── Panel derecho: formulario ────────────────────────────────── */
.login-form-side {
  width: 480px;
  flex-shrink: 0;
  background: #0f172a;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 56px 52px;
  position: relative;
  gap: 0;
}

/* Acento superior azul */
.login-form-side__accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(to right, transparent, #0284c7, transparent);
}

/* ── Logo ─────────────────────────────────────────────────────── */
.login-logo {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 44px;
  animation: loginFadeUp 0.4s ease 0.05s both;
}

.login-logo__circle {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: #0284c7;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 22px;
  box-shadow: 0 0 0 10px rgba(2, 132, 199, 0.14),
    0 10px 32px rgba(2, 132, 199, 0.35);
}

.login-logo__sub {
  color: rgba(255, 255, 255, 0.65);
  font-size: 13px;
  font-weight: 400;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
}

.login-logo__main {
  color: #fff;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

/* ── Formulario ───────────────────────────────────────────────── */
.login-form {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: loginFadeUp 0.4s ease 0.12s both;
}

/* ── Error ────────────────────────────────────────────────────── */
.login-error {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.25);
  border-radius: 7px;
  padding: 9px 12px;
  color: #f87171;
  font-size: 12px;
  font-weight: 500;
  opacity: 0;
  transform: translateY(-4px);
  transition: opacity 0.2s, transform 0.2s;
  pointer-events: none;
}
.login-error--visible {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

/* ── Fields ───────────────────────────────────────────────────── */
.login-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.login-field__label {
  color: rgba(255, 255, 255, 0.75);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* Element UI input overrides */
.login-input :deep(.el-input__inner) {
  background: rgba(255, 255, 255, 0.08);
  border: 1.5px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 13px 16px;
  color: #fff;
  font-size: 15px;
  height: 50px;
  transition: border-color 0.18s, background 0.18s, box-shadow 0.18s;
}

.login-input :deep(.el-input__inner::placeholder) {
  color: rgba(255, 255, 255, 0.35);
}

.login-input :deep(.el-input__inner:focus) {
  border-color: #0284c7;
  background: rgba(2, 132, 199, 0.07);
  box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.15);
}

.login-input--error :deep(.el-input__inner) {
  border-color: rgba(239, 68, 68, 0.5);
}

/* Password show/hide icon */
.login-input :deep(.el-input__suffix) {
  color: rgba(255, 255, 255, 0.3);
}

/* ── Botón login ──────────────────────────────────────────────── */
.login-btn {
  margin-top: 8px;
  width: 100%;
  padding: 15px;
  background: #0284c7;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.18s, box-shadow 0.18s;
  box-shadow: 0 4px 18px rgba(2, 132, 199, 0.45);
  letter-spacing: 0.02em;
}

.login-btn:hover {
  background: #0369a1;
  box-shadow: 0 6px 20px rgba(2, 132, 199, 0.5);
}

.login-btn:active {
  background: #0369a1;
  box-shadow: 0 2px 8px rgba(2, 132, 199, 0.4);
}

/* ── Footer ───────────────────────────────────────────────────── */
.login-footer {
  margin-top: 32px;
  text-align: center;
  color: rgba(255, 255, 255, 0.35);
  font-size: 12px;
  animation: loginFadeUp 0.4s ease 0.2s both;
}

/* ── OAuth2 screen ────────────────────────────────────────────── */
.oauth2-screen {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0f172a;
}

.oauth2-screen__panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
}

.oauth2-screen__logo {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #0284c7;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 8px rgba(2, 132, 199, 0.12),
    0 8px 24px rgba(2, 132, 199, 0.3);
  margin-bottom: 8px;
}

.oauth2-screen__title {
  font-size: 22px;
  font-weight: 700;
  color: #fff;
}

.oauth2-screen__text {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.oauth2-screen__spinner {
  width: 24px;
  height: 24px;
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-top-color: #0284c7;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
  margin-top: 8px;
}

/* ── Animación entrada ────────────────────────────────────────── */
@keyframes loginFadeUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ── Responsive ───────────────────────────────────────────────── */
@media (max-width: 700px) {
  .login-photo {
    display: none;
  }
  .login-form-side {
    width: 100%;
    padding: 48px 32px;
  }
}
</style>
